/**
 * statusVocabulary — the one shared live-status vocabulary for the operator surfaces
 * (DIGEMP-FINISH-PRODUCT-EXPERIENCE-V2-140).
 *
 * Chip labels/classes and the truthful source-state classifier used by the live operational
 * surfaces (the System Map and the employee flow map). Free of JSX and of any data access, so
 * every surface renders the same status word, the same tone and the same
 * loading/fresh/stale/failed semantics from one place.
 *
 * "unknown" and "no_evidence" stay deliberately different: unknown = the source failed or is not
 * readable; no_evidence = the source loaded and reported that no evidence exists on this build.
 */

// The chip shell shared by every live-status chip (size, shape, border). Surface-specific chip
// variants that intentionally differ (larger padding, icon chips) keep their own shell.
export const CHIP_CLS =
  "text-[11px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap";

export const LIVE_STATUS_CLS = {
  active: "bg-green-100 text-green-800 border-green-200",
  // §14C.1 names LIVE and WORKING as two states, and this product reads them as two facts: a
  // component that is live (`active`) is not the same claim as a unit of work in progress. The
  // violet in-progress tone is the one this codebase already used locally for exactly that claim
  // (TicketEmployeeWorkbench's ACTIVE_CHIP, EmployeeWorkspace's QUEUE_BUCKETS), so it is promoted
  // here rather than invented.
  working: "bg-violet-100 text-violet-800 border-violet-200",
  // §14C.1's VERIFIED reading for a finished unit of work. Emerald rather than `active`'s green:
  // "this entity is live" and "this work finished successfully" must not draw the same chip.
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  idle: "bg-gray-100 text-gray-600 border-gray-200",
  waiting_human: "bg-orange-50 text-orange-800 border-orange-200",
  approval_required: "bg-blue-100 text-blue-800 border-blue-200",
  requires_configuration: "bg-amber-100 text-amber-800 border-amber-200",
  not_connected: "bg-rose-100 text-rose-800 border-rose-200",
  blocked: "bg-red-100 text-red-800 border-red-200",
  disabled: "bg-gray-200 text-gray-600 border-gray-300",
  no_evidence: "bg-gray-100 text-gray-500 border-dashed border-gray-300",
  unknown: "bg-gray-100 text-gray-500 border-dashed border-gray-300",
  no_permission: "bg-gray-100 text-gray-500 border-dashed border-gray-300",
  loading: "bg-gray-50 text-gray-400 border-gray-200",
};

// waiting_human's contextual wording is chosen by the consuming surface (the flow map phrases it
// per stage); this is the generic label for the key.
export const LIVE_STATUS_META = {
  active: { label: "פעיל", cls: LIVE_STATUS_CLS.active },
  working: { label: "בעבודה", cls: LIVE_STATUS_CLS.working },
  completed: { label: "הושלם", cls: LIVE_STATUS_CLS.completed },
  idle: { label: "אין עבודה כרגע", cls: LIVE_STATUS_CLS.idle },
  waiting_human: { label: "ממתין לאדם", cls: LIVE_STATUS_CLS.waiting_human },
  approval_required: { label: "דורש אישור", cls: LIVE_STATUS_CLS.approval_required },
  requires_configuration: { label: "דורש הגדרה", cls: LIVE_STATUS_CLS.requires_configuration },
  not_connected: { label: "לא מחובר", cls: LIVE_STATUS_CLS.not_connected },
  blocked: { label: "חסום — מצב חירום", cls: LIVE_STATUS_CLS.blocked },
  disabled: { label: "כבוי", cls: LIVE_STATUS_CLS.disabled },
  no_evidence: { label: "אין ראיות", cls: LIVE_STATUS_CLS.no_evidence },
  unknown: { label: "לא ידוע", cls: LIVE_STATUS_CLS.unknown },
  no_permission: { label: "אין הרשאה", cls: LIVE_STATUS_CLS.no_permission },
  loading: { label: "טוען…", cls: LIVE_STATUS_CLS.loading },
};

export const statusChipMeta = (key) => LIVE_STATUS_META[key] || LIVE_STATUS_META.unknown;

export const SOURCE_STATES = {
  loading: "loading",
  fresh: "fresh",
  stale: "stale",
  failed: "failed",
};

/**
 * Classify a react-query result into the four truthful source states:
 *  - loading: no successful load yet and no failure either;
 *  - fresh:   the last load succeeded;
 *  - stale:   a refresh failed after an earlier successful load — the surface still holds that
 *             earlier payload and must say so instead of presenting it as current;
 *  - failed:  no usable payload at all (never loaded, or loaded empty and then failed).
 * Pure react-query telemetry (isError / data / dataUpdatedAt); no invented state.
 */
export function classifySource(query) {
  if (!query) return { key: SOURCE_STATES.loading, since: null };
  if (query.isError) {
    return query.data != null && query.dataUpdatedAt > 0
      ? { key: SOURCE_STATES.stale, since: query.dataUpdatedAt }
      : { key: SOURCE_STATES.failed, since: null };
  }
  if (query.dataUpdatedAt > 0) return { key: SOURCE_STATES.fresh, since: query.dataUpdatedAt };
  return { key: SOURCE_STATES.loading, since: null };
}

export const isSourceStale = (query) => classifySource(query).key === SOURCE_STATES.stale;
